import type { PitchedItemDto } from '../../lib/reference';
import { fullDifficultyName } from '../../lib/bossGrouping';
import { PitchedItemIcon } from './PitchedItemIcon';

interface PitchedItemRowProps {
  item: PitchedItemDto;
  /** Shown as microtext when the row isn't already nested under a per-boss group heading (the Brilliant list). */
  sourceBossName?: string;
  obtained: boolean;
  pending: boolean;
  onToggle: () => void;
}

/**
 * One Pitched/Brilliant Boss Set item row (REV6 — docs/specs/REV6-pitched-tracker.md): name, equip
 * slot, source difficulties, an optional "Box" chip, and an obtained toggle using REV2's
 * toggle-badge idiom (`CharacterBossRow`'s "Cleared" badge: `aria-pressed` + accent-dim when on).
 * Retired items (Black Heart) render muted with a "no longer drops" note but stay toggleable —
 * players may still own a legacy copy.
 */
export function PitchedItemRow({ item, sourceBossName, obtained, pending, onToggle }: PitchedItemRowProps) {
  return (
    <li
      className={[
        'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2',
        item.retired ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <PitchedItemIcon slug={item.slug} name={item.name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text">{item.name}</p>
          <p className="truncate text-xs text-text-faint">
            {item.equipSlot} · {item.sourceDifficulties.map(fullDifficultyName).join(' · ')}
            {sourceBossName ? ` · ${sourceBossName}` : ''}
            {item.retired && <span className="text-warning"> · no longer drops</span>}
            {!item.verified && (
              <span className="underline decoration-dotted decoration-warning/70 underline-offset-2"> · unverified</span>
            )}
            {item.sourceBossSlug === 'bellona' && <span className="font-semibold text-text-muted"> · KMS</span>}
          </p>
        </div>
      </div>

      {item.boxObtainable && (
        <span
          title="Also drops from the Chaos Black Accessory Box (Kaling, Malefic Star, Bellona, Limbo, Baldrix, Jupiter)"
          className="rounded-full border border-border-strong bg-bg-elevated px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-text-faint"
        >
          Box
        </span>
      )}

      <button
        type="button"
        disabled={pending}
        aria-pressed={obtained}
        aria-label={`${item.name} obtained`}
        onClick={onToggle}
        className={[
          'flex min-h-tap min-w-tap items-center gap-1 rounded-full px-2.5 text-[0.625rem] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          obtained
            ? 'bg-accent-dim text-accent-strong'
            : 'border border-border-strong bg-bg-elevated text-text-faint hover:bg-bg-elevated-hover',
        ].join(' ')}
      >
        {obtained && <span aria-hidden="true">✓</span>}
        Obtained
      </button>
    </li>
  );
}
